<template>
    <div class="single-product-panel-item">
        <div class="image" :style="{ backgroundImage: imageOrPlaceholder(product.image) }"></div>
        <g-level class="title">
            <g-typography tag="h1" font-weight="semibold" :value="product.name" />
        </g-level>
        <g-level class="price">
            <g-level-item><g-typography v-if="showPrices" tag="h3" font-weight="normal" :value="price" /></g-level-item>
            <g-level-item v-if="outOfStock" slot="right"><g-tag color="red"> Out of Stock </g-tag></g-level-item>
        </g-level>
        <div class="variants">
            <div v-for="option in product.options" :key="'option-select.' + option.id" class="option-row">
                <g-select
                    :placeholder="decodeHtml(option.name)"
                    style="width: 100%"
                    size="large"
                    :value="getSelectedOption(option)"
                    :disabled="!isOptionEditable(option.id)"
                    @select="onOptionSelect($event, option.id)"
                >
                    <g-select-option v-for="value in option.values" :key="value.id" :value="value.name">
                        <div v-html="$sanitize(value.name)" />
                    </g-select-option>
                </g-select>
            </div>
        </div>
        <div v-if="product.requirements" class="description">
            <g-typography v-html="$sanitize(product.requirements)" />
        </div>
        <g-button
            class="submit-button"
            shape="round"
            theme="live"
            type="primary"
            size="xlarge"
            :disabled="addButtonDisabled"
            block
            @click="handleContinue"
        >
            {{ buttonText }}
        </g-button>
    </div>
</template>

<script>
    import { get } from 'lodash';
    import ActivationBriefProductConfigMixin from '../../mixins/ActivationBriefProductConfigMixin';
    import { INFLUENCER_CHOICE_VALUE } from '../../../../../constants/activation-products.js';
    import { formatCurrency } from '../../../../../helpers/formatters';

    export default {
        name: 'SingleProductPanelItem',
        props: {
            product: {
                type: Object,
                required: true
            },
            existingValues: {
                type: Array,
                default: () => []
            },
            updateMode: {
                type: Boolean,
                default: false
            },
            panelSlot: {
                type: Object,
                default: () => ({})
            }
        },
        mixins: [ActivationBriefProductConfigMixin],
        data() {
            return {
                options: []
            };
        },
        computed: {
            buttonText() {
                return this.updateMode ? 'Update Product' : 'Add Product';
            },
            outOfStock() {
                return this.productVariant ? !this.isVariantInStock : !this.product.is_in_stock;
            },
            isVariantInStock() {
                return get(this.productVariant, 'is_in_stock', false);
            },
            allOptionsSelected() {
                return this.options.length === this.product.options.length;
            },
            addButtonDisabled() {
                if (this.outOfStock) {
                    return true;
                }
                return !this.allOptionsSelected;
            },
            configItem() {
                return this.getProductConfigItem('product_id', this.product.id, this.panelSlot && this.panelSlot.id);
            },
            productVariant() {
                if (!this.allOptionsSelected) {
                    return null;
                }

                return this.getProductVariantFromOptionValues(this.options);
            },
            price() {
                const price = get(this.productVariant, 'price', this.product.price);

                return formatCurrency(price, get(this, 'brief.brand.currency', 'usd'));
            }
        },
        methods: {
            getProductVariantFromOptionValues(optionValues) {
                const variants = get(this.product, 'variants', []);

                return variants.find((variant) => {
                    return variant.option_values.every((variantOptionValue) => {
                        const optionValue = optionValues.find((val) => val.id === variantOptionValue.option_id);
                        return optionValue && optionValue.value_id === variantOptionValue.id;
                    });
                });
            },
            getSelectedOption(option) {
                const existingSelection = this.options.find((x) => x.id === option.id);
                return existingSelection ? this.decodeHtml(existingSelection.name) : undefined;
            },
            onOptionSelect(valueName, optionId) {
                const option = this.product.options.find((option) => option.id === optionId);
                const value = option.values.find((value) => value.name === valueName);

                const existingSelectionIdx = this.options.findIndex((option) => option.id === optionId);
                if (existingSelectionIdx > -1) {
                    const newSelection = {
                        ...this.options[existingSelectionIdx],
                        value_id: value.id,
                        name: value.name
                    };
                    this.$set(this.options, existingSelectionIdx, newSelection);
                } else {
                    this.options.push({ id: optionId, value_id: value.id, name: value.name });
                }
            },
            handleContinue() {
                if (this.addButtonDisabled) {
                    return;
                }
                this.updateMode ? this.updateProduct() : this.addProduct();
            },
            updateProduct() {
                this.$emit('updated', {
                    payload: {
                        id: this.product.id,
                        variant_id: this.productVariant?.id,
                        options: this.options
                    }
                });
            },
            addProduct() {
                this.$emit('added', {
                    payload: {
                        id: this.product.id,
                        variant_id: this.productVariant?.id,
                        options: this.options
                    },
                    product: this.product
                });
            },
            isOptionEditable(optionId) {
                const configOption = get(this.configItem, 'product_option_values', []).find(
                    (option) => option.option_id === optionId
                );
                // If no config option is present, then all options can be chosen.
                return !configOption || configOption.value === INFLUENCER_CHOICE_VALUE.value;
            },
            decodeHtml: function (stringToEscape) {
                var txt = document.createElement('textarea');
                txt.innerHTML = stringToEscape;
                return txt.value;
            }
        },
        created() {
            const existingValues = this.existingValues || get(this.product, 'variants.0.options', []);

            this.options = existingValues.map((option) => ({
                id: option.option_id || option.id,
                name: option.label || option.name,
                value_id: option.value || option.value_id
            }));
        }
    };
</script>

<style lang="scss" scoped>
    .single-product-panel-item {
        .image {
            width: 100%;
            height: 450px;
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            border: 1px solid $g-color-grey-4;
            border-radius: $g-border-radius;
            margin-bottom: $g-spacing-5;
        }
        .price {
            margin-bottom: $g-spacing-5;
        }
        .description {
            margin-top: $g-spacing-4;
            margin-bottom: $g-spacing-3;
        }
        .variants {
            margin-bottom: $g-spacing-2;
        }
        .submit-button {
            margin-top: $g-spacing-2;
            margin-bottom: $g-spacing-6;
        }
        .option-row {
            margin-bottom: $g-spacing-4;
        }
    }
</style>
